import type { LiveRow, LiveZone } from "./types";

/**
 * Pure UI logic for the live-service surfaces (tasks/live-service-and-floor.md) — kept out of the React
 * components so it is unit-testable with no DOM. The server stamps `lateAtSec` (= slot + venue
 * gracePeriod, §C.4) and `stuck`; the client only RENDERS these on a one-minute ticker and NEVER
 * mutates status off them (R4 — a late flag is visual only, no auto-cancel/auto-release path exists).
 */

/** A booking is "late" when it is still CONFIRMED, has not arrived, and now has passed its lateAt (R4). */
export function isLate(row: LiveRow, nowSec: number): boolean {
  return row.status === "CONFIRMED" && row.arrivedAtSec === null && nowSec >= row.lateAtSec;
}

/** Minutes a booking is past its grace window (>= 0), for the "N min late" badge. */
export function minutesLate(row: LiveRow, nowSec: number): number {
  return Math.max(0, Math.floor((nowSec - row.lateAtSec) / 60));
}

/** Minutes a table has been in cleaning (from the fact `sinceSec`), for the stuck-cleaning badge. */
export function cleaningElapsedMin(sinceSec: number | null, nowSec: number): number {
  if (sinceSec === null) return 0;
  return Math.max(0, Math.floor((nowSec - sinceSec) / 60));
}

/**
 * A zone's occupancy meter: the effective ceiling is `legalCapacity ?? capacity`; `ratio` is clamped
 * to [0,1] for the bar width, `pct` is the rounded percentage. `over` marks a breach of the legal
 * ceiling (an over-capacity flag, FL-4). `nearLimit` comes from the server (>= 90%).
 */
export interface OccupancyMeter {
  occupied: number;
  ceiling: number | null;
  ratio: number;
  pct: number | null;
  nearLimit: boolean;
  over: boolean;
}

export function occupancyMeter(zone: LiveZone): OccupancyMeter {
  const ceiling = zone.legalCapacity ?? zone.capacity ?? null;
  if (ceiling === null || ceiling <= 0) {
    return {
      occupied: zone.occupiedCovers,
      ceiling: null,
      ratio: 0,
      pct: null,
      nearLimit: false,
      over: false,
    };
  }
  const ratio = Math.min(1, zone.occupiedCovers / ceiling);
  return {
    occupied: zone.occupiedCovers,
    ceiling,
    ratio,
    pct: Math.round((zone.occupiedCovers / ceiling) * 100),
    nearLimit: zone.nearLimit,
    over: zone.occupiedCovers > ceiling,
  };
}

/** The four derived/recorded table states carry a stable tint token key (light + dark themed in CSS). */
export function tableStateTint(state: string): "available" | "reserved" | "occupied" | "cleaning" {
  switch (state) {
    case "OCCUPIED":
      return "occupied";
    case "CLEANING":
      return "cleaning";
    case "RESERVED":
      return "reserved";
    default:
      return "available";
  }
}
